import { useEffect, useId, useState } from "react";
import {
  centsFromInput,
  formatCount,
  formatMoney,
  MAX_SPOT_LENGTH,
  normaliseMarkup,
  priceFromCents,
  priceWithMarkup,
  RETIRE_REASON_LABELS,
  RETIRE_REASONS,
  VOID_SERIAL_SUFFIX,
  todayLocal,
  type Client,
  type OpenDeployment,
  type RetireReason,
  type StockRow,
} from "@iltocat/shared";
import { ApiError, api } from "../lib/api.js";
import { Icon } from "./Icon.js";
import { Notice } from "./Notice.js";
import { Panel } from "./Panel.js";
import { useToast } from "./Toast.js";

/**
 * The things that move stock: deploy, move between clients, return, retire --
 * and pricing it from cost, which moves nothing but lives with them.
 * Each is a panel that says what it is about to do, for one line or several, shared by the Stock screen (acting on
 * what was picked from a list) and the item screen (acting on the one thing
 * being looked at), so the two cannot drift apart.
 */

/* ---------------------------------------------------------------- client -- */

/**
 * Which client, or a new one typed in without leaving the panel: deploying
 * and moving both need it, and both are where a new site first turns up.
 */
function ClientField({
  clients,
  value,
  label,
  onChange,
  onClients,
  onError,
}: {
  clients: Client[];
  value: string;
  label: string;
  onChange: (id: string) => void;
  onClients: (clients: Client[]) => void;
  onError: (message: string | null) => void;
}) {
  const [newClient, setNewClient] = useState("");

  async function addClient() {
    const name = newClient.trim();
    if (!name) return;
    onError(null);
    try {
      const { clients: next } = await api.clients.create(name);
      onClients(next);
      onChange(next.find((one) => one.name.toLowerCase() === name.toLowerCase())?.id ?? "");
      setNewClient("");
    } catch (problem) {
      onError(problem instanceof ApiError ? problem.message : "Could not add that client.");
    }
  }

  return (
    <div className="field client-field">
      <label className="field">
        <span className="field-label">{label}</span>
        <select className="input" value={value} onChange={(event) => onChange(event.target.value)}>
          <option value="">Pick a client</option>
          {clients.map((client) => (
            <option key={client.id} value={client.id}>
              {client.name}
            </option>
          ))}
        </select>
      </label>
      <div className="inline-add">
        <Icon name="plus" size={18} />
        <input
          className="input"
          aria-label="A new client's name"
          placeholder="Or add a new client"
          value={newClient}
          onChange={(event) => setNewClient(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              void addClient();
            }
          }}
        />
        <button
          type="button"
          className="button button-quiet"
          disabled={!newClient.trim()}
          onClick={() => void addClient()}
        >
          Add
        </button>
      </div>
    </div>
  );
}

/**
 * Where at the client, optional and free text: a desk, a room, a person
 * working remotely. Offers back what the stockroom has typed before, so the
 * third laptop to "Remote" is one tap, without a list of spots to set up.
 */
function SpotField({
  value,
  onChange,
  spots,
  label = "Where at the client (optional)",
}: {
  value: string;
  onChange: (spot: string) => void;
  spots: string[];
  label?: string;
}) {
  const listId = useId();
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      <input
        className="input"
        maxLength={MAX_SPOT_LENGTH}
        placeholder="Desk 14, Reception, Remote: J. Smith"
        list={spots.length ? listId : undefined}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
      {spots.length > 0 && (
        <datalist id={listId}>
          {spots.map((spot) => (
            <option key={spot} value={spot} />
          ))}
        </datalist>
      )}
    </label>
  );
}

/** Every spot already used, most used first: what `SpotField` offers back. */
export const knownSpots = (lines: { spot: string }[]): string[] => {
  const counts = new Map<string, number>();
  for (const { spot } of lines) if (spot) counts.set(spot, (counts.get(spot) ?? 0) + 1);
  return [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).map(([spot]) => spot);
};

/** Runs one write per thing, in order, and keeps the ones that failed with the sentence each got. */
async function eachOf<T>(
  things: readonly T[],
  write: (thing: T) => Promise<void>,
): Promise<{ thing: T; message: string }[]> {
  const failed: { thing: T; message: string }[] = [];
  for (const thing of things) {
    try {
      await write(thing);
    } catch (problem) {
      failed.push({
        thing,
        message: problem instanceof ApiError ? problem.message : "That did not go through.",
      });
    }
  }
  return failed;
}

/* ---------------------------------------------------------------- deploy -- */

interface DeployLineDraft {
  row: StockRow;
  quantity: string;
  /** Whole percent, as typed. Starts at the standard markup. */
  markup: string;
  /** What the carrier charged for the whole line, as typed. Billed to the client at cost, as a line of its own. */
  shipping: string;
  price: string;
  /** True once the price was typed over: until the markup is changed again, it stays as typed. */
  manual: boolean;
  billable: boolean;
  /** Said out loud: send units held for another client's reserve. */
  take: boolean;
}

/**
 * What a row can send to one client without touching anybody else's reserve:
 * everything on the shelf but what other clients hold. What this client
 * holds itself goes out first, and is worth saying so.
 */
function reach(row: StockRow, clientId: string): { own: number; usable: number; others: string[] } {
  const own = row.holds
    .filter((hold) => hold.clientId === clientId)
    .reduce((sum, hold) => sum + hold.quantity, 0);
  const elsewhere = row.holds.filter((hold) => hold.clientId !== clientId);
  return {
    own,
    usable: row.onHand - elsewhere.reduce((sum, hold) => sum + hold.quantity, 0),
    others: elsewhere.map((hold) => hold.clientName),
  };
}

/** The price a line's cost and markup add up to, as the text the price box holds. Shipping is not in it: it is billed as its own line. */
const autoPrice = (line: Pick<DeployLineDraft, "row" | "markup">): string =>
  priceFromCents(priceWithMarkup(line.row.unitCostCents, normaliseMarkup(line.markup)));

export function DeployPanel({
  rows,
  clients,
  markup,
  spots = [],
  onClients,
  onCancel,
  onDone,
}: {
  rows: StockRow[];
  clients: Client[];
  markup: number;
  /** Where-at-client notes already in use, offered back as suggestions. */
  spots?: string[];
  onClients: (clients: Client[]) => void;
  onCancel: () => void;
  onDone: () => void;
}) {
  const [clientId, setClientId] = useState(clients[0]?.id ?? "");
  const [deployedOn, setDeployedOn] = useState(todayLocal());
  const [spot, setSpot] = useState("");
  const [lines, setLines] = useState<DeployLineDraft[]>(() =>
    rows.map((row) => {
      const line = { row, markup: String(markup) };
      return {
        ...line,
        quantity: "1",
        shipping: "",
        price: autoPrice(line),
        manual: false,
        billable: true,
        take: false,
      };
    }),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();

  const change = (index: number, patch: Partial<DeployLineDraft>) =>
    setLines((now) =>
      now.map((line, at) => {
        if (at !== index) return line;
        const next = { ...line, ...patch };
        return next.manual ? next : { ...next, price: autoPrice(next) };
      }),
    );

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    if (!clientId) return setError("Pick a client to deploy to.");

    const payload = [];
    for (const line of lines) {
      const quantity = Number(line.quantity);
      const unitPriceCents = centsFromInput(line.price);
      const name = line.row.title || "Untitled item";
      if (!Number.isInteger(quantity) || quantity < 1 || quantity > line.row.onHand) {
        return setError(`${name}: send between 1 and ${line.row.onHand}.`);
      }
      if (line.shipping.trim() && centsFromInput(line.shipping) === null) {
        return setError(`${name}: the shipping is not a number.`);
      }
      if (unitPriceCents === null) return setError(`${name} needs a bill-out price. Use 0 for none.`);
      const { usable, others } = reach(line.row, clientId);
      const taking = quantity > usable;
      if (taking && !line.take) {
        return setError(
          `${name} is held for ${others.join(" and ")}. Tick “Take from their reserve” to send it anyway.`,
        );
      }
      const shippingCents = centsFromInput(line.shipping) ?? 0;
      payload.push({
        itemId: line.row.itemId,
        quantity,
        unitPriceCents,
        shippingCents,
        billable: line.billable,
        ...(taking ? { takeReserved: true } : {}),
      });
    }

    setBusy(true);
    try {
      await api.stock.deploy({
        clientId,
        deployedOn,
        lines: payload,
        ...(spot.trim() ? { spot: spot.trim() } : {}),
      });
      toast(`Deployed ${payload.length === 1 ? "1 line" : `${payload.length} lines`}.`);
      onDone();
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "That did not deploy.");
      setBusy(false);
    }
  }

  const total = lines.reduce(
    (sum, line) =>
      sum +
      (line.billable
        ? (centsFromInput(line.price) ?? 0) * (Number(line.quantity) || 0) +
          (centsFromInput(line.shipping) ?? 0)
        : 0),
    0,
  );

  return (
    <Panel
      reveal
      title="Deploy to a client"
      note={`Prices start at cost plus your standard ${markup}% markup. Change a line's markup or type over its price. Shipping is billed to the client at cost, as a line of its own. Units from a reserve this client paid for in advance go out without billing again.`}
    >
      <form className="stack" onSubmit={submit}>
        <div className="split">
          <ClientField
            clients={clients}
            value={clientId}
            label="Client"
            onChange={setClientId}
            onClients={onClients}
            onError={setError}
          />
          <label className="field">
            <span className="field-label">Went out on</span>
            <input
              className="input"
              type="date"
              required
              value={deployedOn}
              onChange={(event) => setDeployedOn(event.target.value)}
            />
          </label>
        </div>
        <SpotField value={spot} onChange={setSpot} spots={spots} />

        <table className="table">
          <thead>
            <tr>
              <th>Item</th>
              <th>Units</th>
              <th>Cost each</th>
              <th>Markup %</th>
              <th>Shipping (whole line)</th>
              <th>Price each</th>
              <th>Bill it</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line, index) => {
              const { own, usable, others } = reach(line.row, clientId);
              const wanted = line.row.tracking === "bulk" ? Number(line.quantity) || 0 : 1;
              const client = clients.find((one) => one.id === clientId)?.name ?? "";
              return (
                <tr key={line.row.itemId}>
                  <td data-label="Item">
                    <span className="deploy-item">
                      <span>
                        {line.row.title || "Untitled item"}
                        {line.row.serial && <span className="muted mono"> · {line.row.serial}</span>}
                      </span>
                      {own > 0 && clientId && (
                        <span className="held-badge held-badge-own">
                          <Icon name="bookmark" size={12} />
                          From {client}’s reserve
                        </span>
                      )}
                      {wanted > usable && (
                        <label className="deploy-take">
                          <input
                            type="checkbox"
                            className="check"
                            checked={line.take}
                            onChange={(event) => change(index, { take: event.target.checked })}
                          />
                          <span>
                            Held for {others.join(" and ")}. Take from their reserve
                            {line.row.tracking === "bulk"
                              ? ` (${wanted - Math.max(usable, 0)} of ${wanted})`
                              : ""}
                          </span>
                        </label>
                      )}
                    </span>
                  </td>
                  <td data-label="Units">
                    {line.row.tracking === "bulk" ? (
                      <input
                        className="input input-small input-number"
                        type="number"
                        min={1}
                        max={line.row.onHand}
                        aria-label={`Units of ${line.row.title}`}
                        value={line.quantity}
                        onChange={(event) => change(index, { quantity: event.target.value })}
                      />
                    ) : (
                      "1"
                    )}
                  </td>
                  <td data-label="Cost each" className="muted">
                    {line.row.unitCostCents === null ? "n/a" : formatMoney(line.row.unitCostCents)}
                  </td>
                  <td data-label="Markup %">
                    <input
                      className="input input-small input-number"
                      inputMode="numeric"
                      aria-label={`Markup percent for ${line.row.title}`}
                      value={line.markup}
                      onChange={(event) => change(index, { markup: event.target.value, manual: false })}
                    />
                  </td>
                  <td data-label="Shipping">
                    <input
                      className="input input-small input-number"
                      inputMode="decimal"
                      placeholder="0.00"
                      aria-label={`Shipping for ${line.row.title}`}
                      value={line.shipping}
                      onChange={(event) => change(index, { shipping: event.target.value })}
                    />
                  </td>
                  <td data-label="Price each">
                    <input
                      className="input input-small input-number"
                      inputMode="decimal"
                      aria-label={`Price for ${line.row.title}`}
                      value={line.price}
                      onChange={(event) => change(index, { price: event.target.value, manual: true })}
                    />
                  </td>
                  <td data-label="Bill it">
                    <label className="toggle">
                      <input
                        type="checkbox"
                        checked={line.billable}
                        onChange={(event) => change(index, { billable: event.target.checked })}
                      />
                      <span className="toggle-track" />
                      <span className="sr-only">Bill {line.row.title}</span>
                    </label>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {error && <Notice tone="error">{error}</Notice>}
        <div className="panel-foot">
          <span className="muted">To bill: {formatMoney(total)}</span>
          <button type="button" className="button button-quiet" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="button" disabled={busy}>
            {busy ? "Deploying…" : "Deploy"}
          </button>
        </div>
      </form>
    </Panel>
  );
}

/* ------------------------------------------------ move between clients -- */

interface MoveLineDraft {
  line: OpenDeployment;
  quantity: string;
  price: string;
  billable: boolean;
}

/**
 * Straight from one client to another, for one line or everything ticked. The
 * server records it as a return and a new deployment on the same day, so
 * nothing has to go back to the shelf first. Each line's price starts at what
 * it went out at last time, and billing is on: moving a laptop to Branch B is
 * usually Branch B's to pay for, and a line that is not can be switched off.
 */
export function TransferPanel({
  lines,
  clients,
  spots = [],
  onClients,
  onCancel,
  onDone,
}: {
  lines: OpenDeployment[];
  clients: Client[];
  spots?: string[];
  onClients: (clients: Client[]) => void;
  onCancel: () => void;
  onDone: () => void;
}) {
  const from = new Set(lines.map((line) => line.clientId));
  const [clientId, setClientId] = useState(clients.find((client) => !from.has(client.id))?.id ?? "");
  const [movedOn, setMovedOn] = useState(todayLocal());
  const [spot, setSpot] = useState("");
  const [drafts, setDrafts] = useState<MoveLineDraft[]>(() =>
    lines.map((line) => ({
      line,
      quantity: String(line.quantity - line.returned),
      price: priceFromCents(line.unitPriceCents) || "0.00",
      billable: true,
    })),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();

  const change = (index: number, patch: Partial<MoveLineDraft>) =>
    setDrafts((now) => now.map((draft, at) => (at === index ? { ...draft, ...patch } : draft)));

  const clientName = clients.find((client) => client.id === clientId)?.name ?? "";

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    if (!clientId) return setError("Pick the client it is going to.");
    const payload = [];
    for (const draft of drafts) {
      const name = draft.line.title || "Untitled item";
      if (draft.line.clientId === clientId) return setError(`${name} is already at ${clientName}.`);
      const open = draft.line.quantity - draft.line.returned;
      const quantity = draft.line.tracking === "bulk" ? Number(draft.quantity) : 1;
      if (!Number.isInteger(quantity) || quantity < 1 || quantity > open) {
        return setError(`${name}: move between 1 and ${open}.`);
      }
      const unitPriceCents = centsFromInput(draft.price);
      if (unitPriceCents === null) return setError(`${name} needs a bill-out price. Use 0 for none.`);
      payload.push({ deploymentId: draft.line.id, quantity, unitPriceCents, billable: draft.billable });
    }
    setBusy(true);
    try {
      await api.stock.transfer({
        clientId,
        movedOn,
        lines: payload,
        ...(spot.trim() ? { spot: spot.trim() } : {}),
      });
      toast(`Moved ${payload.length === 1 ? "1 line" : `${payload.length} lines`} to ${clientName}.`);
      onDone();
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "That did not move.");
      setBusy(false);
    }
  }

  const title =
    lines.length === 1
      ? `Move ${lines[0]!.title || "this item"} to another client`
      : `Move ${lines.length} lines to another client`;

  return (
    <Panel
      reveal
      title={title}
      note="Recorded as back from where it is and out to the new client on the same day, without a stop on the shelf. The old client is not billed for anything that came back before its bill; the new line bills at the price below."
    >
      <form className="stack" onSubmit={submit}>
        <div className="split">
          <ClientField
            clients={clients}
            value={clientId}
            label="Moving to"
            onChange={setClientId}
            onClients={onClients}
            onError={setError}
          />
          <label className="field">
            <span className="field-label">Moved on</span>
            <input
              className="input"
              type="date"
              required
              value={movedOn}
              onChange={(event) => setMovedOn(event.target.value)}
            />
          </label>
        </div>
        <SpotField value={spot} onChange={setSpot} spots={spots} label="Where at the new client (optional)" />

        <table className="table">
          <thead>
            <tr>
              <th>Item</th>
              <th>From</th>
              <th>Units</th>
              <th>Price each</th>
              <th>Bill it</th>
            </tr>
          </thead>
          <tbody>
            {drafts.map((draft, index) => (
              <tr key={draft.line.id}>
                <td data-label="Item">
                  <span>
                    {draft.line.title || "Untitled item"}
                    {draft.line.serial && <span className="muted mono"> · {draft.line.serial}</span>}
                  </span>
                </td>
                <td data-label="From">{draft.line.clientName}</td>
                <td data-label="Units">
                  {draft.line.tracking === "bulk" ? (
                    <input
                      className="input input-small input-number"
                      type="number"
                      min={1}
                      max={draft.line.quantity - draft.line.returned}
                      aria-label={`Units of ${draft.line.title}`}
                      value={draft.quantity}
                      onChange={(event) => change(index, { quantity: event.target.value })}
                    />
                  ) : (
                    "1"
                  )}
                </td>
                <td data-label="Price each">
                  <input
                    className="input input-small input-number"
                    inputMode="decimal"
                    aria-label={`Price for ${draft.line.title}`}
                    value={draft.price}
                    onChange={(event) => change(index, { price: event.target.value })}
                  />
                </td>
                <td data-label="Bill it">
                  <label className="toggle">
                    <input
                      type="checkbox"
                      checked={draft.billable}
                      onChange={(event) => change(index, { billable: event.target.checked })}
                    />
                    <span className="toggle-track" />
                    <span className="sr-only">Bill {draft.line.title}</span>
                  </label>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {error && <Notice tone="error">{error}</Notice>}
        <div className="panel-foot">
          <button type="button" className="button button-quiet" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="button" disabled={busy}>
            {busy ? "Moving…" : clientName ? `Move to ${clientName}` : "Move"}
          </button>
        </div>
      </form>
    </Panel>
  );
}

/* ------------------------------------------------------------------ spot -- */

/**
 * Where at the client, for one deployed line or everything ticked: one note
 * for all of them, or cleared. It moves nothing and bills nothing, so there is
 * no date and no history line; it is the note beside the client's name.
 */
export function SpotPanel({
  lines,
  spots,
  onCancel,
  onDone,
}: {
  lines: OpenDeployment[];
  spots: string[];
  onCancel: () => void;
  onDone: () => void;
}) {
  const [pending, setPending] = useState(lines);
  const shared = new Set(lines.map((line) => line.spot));
  const [spot, setSpot] = useState(shared.size === 1 ? (lines[0]?.spot ?? "") : "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();
  const one = pending.length === 1 ? pending[0] : undefined;

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    const next = spot.trim();
    const failed = await eachOf(pending, async (line) => {
      await api.stock.setSpot(line.id, next);
    });
    setBusy(false);
    if (failed.length === 0) {
      toast(next ? `Noted: ${next}.` : "Cleared.");
      onDone();
      return;
    }
    setPending(failed.map(({ thing }) => thing));
    setError(failed.map(({ thing, message }) => `${thing.title || "Untitled item"}: ${message}`).join(" "));
  }

  return (
    <Panel
      reveal
      title={
        one
          ? `Where ${one.title || "this item"} is at ${one.clientName}`
          : `Where ${pending.length} lines are`
      }
      note="A desk, a room, or who has it remotely. It is a note beside the client's name, so it moves nothing and bills nothing. Leave it empty to clear it."
    >
      <form className="stack" onSubmit={submit}>
        <SpotField value={spot} onChange={setSpot} spots={spots} label="Where at the client" />
        {!one && (
          <p className="muted small">
            {pending.map((line) => `${line.title || "Untitled item"} at ${line.clientName}`).join(", ")}
          </p>
        )}
        {error && <Notice tone="error">{error}</Notice>}
        <div className="panel-foot">
          <button type="button" className="button button-quiet" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="button" disabled={busy}>
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </Panel>
  );
}

/* ---------------------------------------------------------------- return -- */

/**
 * Back to the shelf, for one line or everything ticked. One date for all of
 * them; a bulk line can bring back part of what went out. Each line is its
 * own write, so one that is refused is named and stays in the panel while the
 * rest go through.
 */
export function ReturnPanel({
  lines,
  onCancel,
  onDone,
}: {
  lines: OpenDeployment[];
  onCancel: () => void;
  onDone: () => void;
}) {
  const [pending, setPending] = useState(lines);
  const [quantities, setQuantities] = useState<Record<string, string>>(() =>
    Object.fromEntries(lines.map((line) => [line.id, String(line.quantity - line.returned)])),
  );
  const [returnedOn, setReturnedOn] = useState(todayLocal());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    const counts = new Map<string, number>();
    for (const line of pending) {
      const open = line.quantity - line.returned;
      const count = line.tracking === "bulk" ? Number(quantities[line.id]) : open;
      if (!Number.isInteger(count) || count < 1 || count > open) {
        return setError(`${line.title || "Untitled item"}: return between 1 and ${open}.`);
      }
      counts.set(line.id, count);
    }
    setBusy(true);
    const failed = await eachOf(pending, async (line) => {
      await api.stock.return({ deploymentId: line.id, quantity: counts.get(line.id)!, returnedOn });
    });
    const back = pending.length - failed.length;
    if (back > 0) toast(back === 1 ? "Back on the shelf." : `${back} lines back on the shelf.`);
    if (failed.length === 0) return onDone();
    setPending(failed.map((one) => one.thing));
    setError(failed.map((one) => `${one.thing.title || "Untitled item"}: ${one.message}`).join(" "));
    setBusy(false);
  }

  const one = pending.length === 1 ? pending[0]! : null;
  return (
    <Panel
      reveal
      title={one ? `Return from ${one.clientName}` : `Return ${pending.length} lines`}
      note={
        one
          ? `${one.title || "This item"} goes back on the shelf as the same item, and this deployment keeps the ${formatMoney(one.unitPriceCents)} it went out at.`
          : "Each goes back on the shelf as the same item, and each deployment keeps the price it went out at."
      }
    >
      <form className="stack" onSubmit={submit}>
        <label className="field">
          <span className="field-label">Came back on</span>
          <input
            className="input"
            type="date"
            required
            value={returnedOn}
            onChange={(event) => setReturnedOn(event.target.value)}
          />
        </label>
        {one && one.tracking === "bulk" && (
          <label className="field">
            <span className="field-label">Units coming back (of {one.quantity - one.returned})</span>
            <input
              className="input input-number"
              type="number"
              min={1}
              max={one.quantity - one.returned}
              value={quantities[one.id] ?? ""}
              onChange={(event) => setQuantities((now) => ({ ...now, [one.id]: event.target.value }))}
            />
          </label>
        )}
        {!one && (
          <ul className="rows batch-list">
            {pending.map((line) => (
              <li key={line.id} className="batch-row">
                <span>
                  {line.title || "Untitled item"}
                  {line.serial && <span className="muted mono"> · {line.serial}</span>}
                  <span className="muted"> · {line.clientName}</span>
                </span>
                {line.tracking === "bulk" && (
                  <input
                    className="input input-small input-number"
                    type="number"
                    min={1}
                    max={line.quantity - line.returned}
                    aria-label={`Units of ${line.title} coming back`}
                    value={quantities[line.id] ?? ""}
                    onChange={(event) => setQuantities((now) => ({ ...now, [line.id]: event.target.value }))}
                  />
                )}
              </li>
            ))}
          </ul>
        )}
        {error && <Notice tone="error">{error}</Notice>}
        <div className="panel-foot">
          <button type="button" className="button button-quiet" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="button" disabled={busy}>
            {busy ? "Returning…" : "Return to stock"}
          </button>
        </div>
      </form>
    </Panel>
  );
}

/* ---------------------------------------------------------------- retire -- */

/**
 * Out of service, for one item or everything ticked: one reason, date, vendor
 * and note for all of them, since a batch that goes to the recycler goes
 * together. A certificate, if given, is attached to each retirement -- one
 * wipe certificate usually covers the whole batch. Each item is its own
 * write; a refused one is named and stays in the panel.
 */
export function RetirePanel({
  rows,
  onCancel,
  onDone,
}: {
  rows: StockRow[];
  onCancel: () => void;
  onDone: () => void;
}) {
  const [pending, setPending] = useState(rows);
  const [quantities, setQuantities] = useState<Record<string, string>>(() =>
    Object.fromEntries(rows.map((row) => [row.itemId, "1"])),
  );
  const [reason, setReason] = useState<RetireReason>("end_of_life");
  const mistake = reason === "entered_by_mistake";
  const [retiredOn, setRetiredOn] = useState(todayLocal());
  const [vendor, setVendor] = useState("");
  const [note, setNote] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    const counts = new Map<string, number>();
    for (const row of pending) {
      const count = row.tracking === "bulk" ? Number(quantities[row.itemId]) : 1;
      if (!Number.isInteger(count) || count < 1 || count > Math.max(row.onHand, 1)) {
        return setError(`${row.title || "Untitled item"}: retire between 1 and ${row.onHand}.`);
      }
      counts.set(row.itemId, count);
    }
    setBusy(true);
    let filesFailed = 0;
    const failed = await eachOf(pending, async (row) => {
      const { retirement } = await api.stock.retire({
        itemId: row.itemId,
        quantity: counts.get(row.itemId)!,
        reason,
        retiredOn,
        disposalVendor: mistake ? "" : vendor,
        note,
      });
      if (file && !mistake) {
        // The retirement stands without it; a certificate that did not land
        // can be added again from the Retired list.
        await api.stock.certificate(retirement.id, file).catch(() => {
          filesFailed += 1;
        });
      }
    });
    const retired = pending.length - failed.length;
    if (retired > 0) {
      const what = retired === 1 ? "Retired." : `Retired ${retired} items.`;
      toast(
        filesFailed > 0
          ? `${what} The certificate did not upload to ${filesFailed}; add it from the Retired list.`
          : what,
      );
    }
    if (failed.length === 0) return onDone();
    setPending(failed.map((one) => one.thing));
    setError(failed.map((one) => `${one.thing.title || "Untitled item"}: ${one.message}`).join(" "));
    setBusy(false);
  }

  const one = pending.length === 1 ? pending[0]! : null;
  return (
    <Panel
      reveal
      title={one ? `Retire ${one.title || "this item"}` : `Retire ${pending.length} items`}
      note="It leaves the stock counts for good but stays findable by serial, with its whole history. Nothing is deleted."
    >
      <form className="stack" onSubmit={submit}>
        <div className="split">
          <label className="field">
            <span className="field-label">Why</span>
            <select
              className="input"
              value={reason}
              onChange={(event) => setReason(event.target.value as RetireReason)}
            >
              {RETIRE_REASONS.map((value) => (
                <option key={value} value={value}>
                  {RETIRE_REASON_LABELS[value]}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="field-label">When</span>
            <input
              className="input"
              type="date"
              required
              value={retiredOn}
              onChange={(event) => setRetiredOn(event.target.value)}
            />
          </label>
          {one && one.tracking === "bulk" && (
            <label className="field">
              <span className="field-label">Units (of {one.onHand} on the shelf)</span>
              <input
                className="input input-number"
                type="number"
                min={1}
                max={one.onHand}
                value={quantities[one.itemId] ?? ""}
                onChange={(event) => setQuantities((now) => ({ ...now, [one.itemId]: event.target.value }))}
              />
            </label>
          )}
          {!mistake && (
            <label className="field">
              <span className="field-label">Disposal vendor (optional)</span>
              <input className="input" value={vendor} onChange={(event) => setVendor(event.target.value)} />
            </label>
          )}
        </div>
        {mistake && (
          <Notice tone="warn">
            {`For something that was never really here: a typo on a receipt, or a serial scanned twice.${
              pending.some((row) => row.tracking === "serialized")
                ? ` A serial number is kept with “${VOID_SERIAL_SUFFIX.trim()}” after it, so the right unit can be received under it, and anything it had waiting to bill is cancelled.`
                : ""
            } It stays in its history and the Retired list, and out of reports. Refused for anything a client was already billed for.`}
          </Notice>
        )}
        {!one && (
          <ul className="rows batch-list">
            {pending.map((row) => (
              <li key={row.itemId} className="batch-row">
                <span>
                  {row.title || "Untitled item"}
                  {row.serial && <span className="muted mono"> · {row.serial}</span>}
                </span>
                {row.tracking === "bulk" && (
                  <label className="batch-units">
                    <input
                      className="input input-small input-number"
                      type="number"
                      min={1}
                      max={row.onHand}
                      aria-label={`Units of ${row.title} to retire`}
                      value={quantities[row.itemId] ?? ""}
                      onChange={(event) =>
                        setQuantities((now) => ({ ...now, [row.itemId]: event.target.value }))
                      }
                    />
                    <span className="muted">of {row.onHand}</span>
                  </label>
                )}
              </li>
            ))}
          </ul>
        )}
        <label className="field">
          <span className="field-label">Note (optional)</span>
          <input className="input" value={note} onChange={(event) => setNote(event.target.value)} />
        </label>
        {!mistake && (
          <label className="field">
            <span className="field-label">
              {one
                ? "Proof of data destruction (optional: a PDF or photo of the certificate)"
                : "Proof of data destruction (optional: attached to each of them)"}
            </span>
            <input
              className="input input-file"
              type="file"
              accept="application/pdf,image/jpeg,image/png,image/webp"
              onChange={(event) => setFile(event.target.files?.[0] ?? null)}
            />
          </label>
        )}
        {error && <Notice tone="error">{error}</Notice>}
        <div className="panel-foot">
          <button type="button" className="button button-quiet" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="button button-danger" disabled={busy}>
            {busy ? "Retiring…" : one ? "Retire" : `Retire ${pending.length}`}
          </button>
        </div>
      </form>
    </Panel>
  );
}

/* ----------------------------------------------------------------- price -- */

/**
 * The item screen's "Set to cost + markup", for what is ticked on the Stock
 * screen. Never done on arrival: the price is public, and at 0% it is the
 * private cost, so it takes somebody pressing this. The counts come from the
 * rows on screen; the server does the actual choosing among those items, so a
 * stale count can be off but a price cannot be.
 */
export function PricePanel({
  rows,
  markup,
  onCancel,
  onDone,
}: {
  /** The stock rows to price: what was ticked, or the one row a menu was opened on. */
  rows: StockRow[];
  markup: number;
  onCancel: () => void;
  onDone: () => void;
}) {
  const [overwrite, setOverwrite] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();

  const costed = rows.filter((row) => row.unitCostCents !== null);
  const unpriced = costed.filter((row) => row.priceCents === null).length;
  const different = costed.filter(
    (row) => row.priceCents !== null && row.priceCents !== priceWithMarkup(row.unitCostCents, markup),
  ).length;
  const noCost = rows.length - costed.length;
  const count = unpriced + (overwrite ? different : 0);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const { priced } = await api.stock.priceFromCost(
        overwrite,
        rows.map((row) => row.itemId),
      );
      toast(
        priced === 0
          ? "Every price already matched."
          : priced === 1
            ? "Priced 1 item."
            : `Priced ${priced} items.`,
      );
      onDone();
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Could not set the prices.");
      setBusy(false);
    }
  }

  return (
    <Panel
      reveal
      title={`Price at cost + ${markup}%`}
      note={`Sets each item's price to its unit cost plus your standard ${markup}% markup. The price is public and goes into listings${markup === 0 ? ", so at 0% it shows what you paid" : ""}. Deployments keep the price they went out at.`}
    >
      <form className="stack" onSubmit={submit}>
        <p>
          {unpriced === 0
            ? "Every item with a unit cost already has a price."
            : unpriced === 1
              ? "1 item has a unit cost but no price."
              : `${unpriced} items have a unit cost but no price.`}
          {noCost > 0 && (
            <span className="muted">
              {" "}
              {noCost === 1 ? "1 item has" : `${noCost} items have`} no unit cost and{" "}
              {noCost === 1 ? "is" : "are"} left alone.
            </span>
          )}
        </p>
        <label className="toggle">
          <input
            type="checkbox"
            checked={overwrite}
            disabled={different === 0}
            onChange={(event) => setOverwrite(event.target.checked)}
          />
          <span className="toggle-track" aria-hidden="true" />
          <span>
            Also replace prices already set
            <span className="toggle-hint">
              {different === 0
                ? `Every priced item is already at cost + ${markup}%.`
                : different === 1
                  ? "1 item is priced at something else. Its price will be replaced."
                  : `${different} items are priced at something else. Their prices will be replaced.`}
            </span>
          </span>
        </label>
        {error && <Notice tone="error">{error}</Notice>}
        <div className="panel-foot">
          <button type="button" className="button button-quiet" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            type="submit"
            className={overwrite ? "button button-danger" : "button"}
            disabled={busy || count === 0}
          >
            {busy ? "Pricing…" : count === 1 ? "Price 1 item" : `Price ${count} items`}
          </button>
        </div>
      </form>
    </Panel>
  );
}

const unitsOf = (row: StockRow): number => row.onHand + row.deployed;
const where = (row: StockRow): string =>
  [
    row.onHand > 0 && `${formatCount(row.onHand)} on the shelf`,
    row.deployed > 0 && `${formatCount(row.deployed)} out`,
    row.holds.length > 0 && "held",
  ]
    .filter(Boolean)
    .join(" · ") || "none on hand";

/**
 * Convert to bulk: one or more items become a single counted line. Started
 * from a serialized item it offers the other stock with the same name, all
 * ticked; from ticked rows on the Stock screen, those; from a bulk line,
 * nothing yet, to find what to merge in. More can be found and added by name
 * either way, and the name kept is chosen (the one with the most units, to
 * start with). What is out at a client or held comes along; retired stock is
 * not offered.
 */
export function ConvertToBulkPanel({
  rows,
  sameName = false,
  onCancel,
  onDone,
}: {
  /** The first is the one this was started from. */
  rows: StockRow[];
  /** Pre-tick the other stock with the first row's name. */
  sameName?: boolean;
  onCancel: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [picked, setPicked] = useState<StockRow[]>(rows);
  const [all, setAll] = useState<StockRow[] | null>(null);
  const [query, setQuery] = useState("");
  const [keep, setKeep] = useState<string>(
    () => [...rows].sort((a, b) => unitsOf(b) - unitsOf(a))[0]!.itemId,
  );
  const first = rows[0]!;

  useEffect(() => {
    let live = true;
    void api.stock
      .overview()
      .then(({ items }) => {
        if (!live) return;
        setAll(items);
        if (sameName) {
          const same = (title: string) => title.trim().toLowerCase() === first.title.trim().toLowerCase();
          setPicked((now) => [
            ...now,
            ...items.filter((one) => same(one.title) && !now.some((row) => row.itemId === one.itemId)),
          ]);
        }
      })
      .catch(() => live && setAll([]));
    return () => {
      live = false;
    };
  }, [sameName, first.title]);

  const units = picked.reduce((sum, row) => sum + unitsOf(row), 0);
  const onShelf = picked.reduce((sum, row) => sum + row.onHand, 0);
  const out = units - onShelf;
  const kept = picked.find((row) => row.itemId === keep) ?? picked[0]!;
  const lonelyLine = picked.length === 1 && first.tracking === "bulk";
  const needle = query.trim().toLowerCase();
  const findable = (all ?? []).filter((one) => !picked.some((row) => row.itemId === one.itemId));
  const matches = needle
    ? findable.filter((one) => `${one.title} ${one.serial}`.toLowerCase().includes(needle)).slice(0, 8)
    : [];

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const ids = [
        kept.itemId,
        ...picked.filter((row) => row.itemId !== kept.itemId).map((row) => row.itemId),
      ];
      await api.stock.merge(ids, todayLocal());
      toast(`${kept.title || "It"} is now one bulk line of ${formatCount(units)}.`);
      onDone();
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "That did not convert.");
      setBusy(false);
    }
  }

  return (
    <Panel
      reveal
      title={
        picked.length === 1
          ? `Convert ${first.title || "this item"} to bulk`
          : `Convert ${picked.length} items to bulk`
      }
      note="One counted line instead of an item each. History, photos and tags move onto the line, what is out at a client or held stays where it is, and any serials are kept in the line's notes."
    >
      <form className="stack" onSubmit={submit}>
        <div className="field">
          <span className="field-label">Merging, and the name to keep</span>
          <ul className="rows batch-list">
            {picked.map((row) => (
              <li key={row.itemId} className="batch-row">
                <label className="radio-row convert-row">
                  <input
                    type="radio"
                    name="keep-name"
                    checked={kept.itemId === row.itemId}
                    onChange={() => setKeep(row.itemId)}
                    aria-label={`Keep the name ${row.title || "Untitled item"}`}
                  />
                  <span className="convert-row-name">
                    {row.title || "Untitled item"}
                    <span className="muted small">
                      {" "}
                      · {row.tracking === "serialized" ? row.serial || "no serial" : "bulk"} · {where(row)}
                    </span>
                  </span>
                  {picked.length > 1 && (
                    <button
                      type="button"
                      className="icon-button icon-button-sm"
                      aria-label={`Leave out ${row.title || "this item"}`}
                      onClick={() => {
                        setPicked((now) => now.filter((one) => one.itemId !== row.itemId));
                        if (keep === row.itemId)
                          setKeep(picked.find((one) => one.itemId !== row.itemId)!.itemId);
                      }}
                    >
                      <Icon name="x" size={14} />
                    </button>
                  )}
                </label>
              </li>
            ))}
          </ul>
        </div>

        <div className="line-picker">
          <label className="searchbox line-picker-find">
            <Icon name="search" size={16} />
            <input
              aria-label="Find more stock to merge in"
              placeholder={all === null ? "Loading…" : "Find more to merge in, by name or serial"}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </label>
          {matches.length > 0 && (
            <ul className="line-picker-list">
              {matches.map((row) => (
                <li key={row.itemId}>
                  <button
                    type="button"
                    className="line-picker-option"
                    onClick={() => {
                      setPicked((now) => [...now, row]);
                      setQuery("");
                    }}
                  >
                    <span>
                      {row.title || "Untitled item"}
                      {row.serial && <span className="muted mono"> · {row.serial}</span>}
                    </span>
                    <span className="muted small">{where(row)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {needle && matches.length === 0 && all !== null && (
            <p className="muted small">Nothing else in stock is called anything like that.</p>
          )}
        </div>

        <p className="receive-summary">
          {lonelyLine
            ? "This is already one bulk line. Find what to merge into it."
            : `Becomes one bulk line called “${kept.title || "Untitled item"}” of ${formatCount(units)}: ${formatCount(onShelf)} on the shelf${out > 0 ? `, ${formatCount(out)} out at clients` : ""}, at the average cost.`}
        </p>
        {error && <Notice tone="error">{error}</Notice>}
        <div className="panel-foot">
          <button type="button" className="button button-quiet" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="button" disabled={busy || lonelyLine}>
            {busy ? "Converting…" : "Convert to bulk"}
          </button>
        </div>
      </form>
    </Panel>
  );
}

/**
 * Convert to serialized: a bulk line becomes one item per unit, each with its
 * details and cost, serials typed or scanned now or filled in later. A serial
 * already on the line is filled in as the first. Not while any of it is out at
 * a client or held: those are counts that would have to be divided by guess.
 */
export function ConvertToSerializedPanel({
  row,
  onCancel,
  onDone,
}: {
  row: StockRow;
  onCancel: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [serials, setSerials] = useState(row.serial);
  const name = row.title || "this item";
  const busyNow = row.holds.length > 0 || row.deployed > 0;
  const typed = serials
    .split(/\r?\n/)
    .map((serial) => serial.trim())
    .filter(Boolean);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.stock.split(row.itemId, typed, todayLocal());
      toast(`${name} is now ${formatCount(row.onHand)} serialized items.`);
      onDone();
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "That did not convert.");
      setBusy(false);
    }
  }

  return (
    <Panel
      reveal
      title={`Convert ${name} to serialized`}
      note="One item per unit, each with this one's details, at the same cost. Photos stay with the first."
    >
      <form className="stack" onSubmit={submit}>
        {busyNow && (
          <Notice tone="warn">
            Some of this line is out at a client or held in a reserve. Return or release it first.
          </Notice>
        )}
        <label className="field">
          <span className="field-label">
            Serials, one per line ({typed.length} of {row.onHand}; leave some out to fill in later)
          </span>
          <textarea
            className="input input-area mono"
            rows={4}
            value={serials}
            onChange={(event) => setSerials(event.target.value)}
          />
        </label>
        <p className="receive-summary">
          {`Becomes ${formatCount(row.onHand)} items, one per unit${typed.length ? `, ${typed.length} with a serial` : ""}.`}
        </p>
        {error && <Notice tone="error">{error}</Notice>}
        <div className="panel-foot">
          <button type="button" className="button button-quiet" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="button" disabled={busy || busyNow || typed.length > row.onHand}>
            {busy ? "Converting…" : "Convert to serialized"}
          </button>
        </div>
      </form>
    </Panel>
  );
}
